import { IconButton } from '@zapier/design-system';
import React, { useContext } from 'react';
import {
  DialogProps,
  ModalOverlayProps as ReactAriaModalOverlayProps,
  OverlayTriggerStateContext as ReactAriaOverlayTriggerStateContext,
} from 'react-aria-components';
import { DialogBase, DialogBaseProps } from '../ModalDialogBase/DialogBase';
import { ModalDialogContext } from '../ModalDialogBase/ModalDialogContext';
import { OverlayBase } from '../ModalDialogBase/OverlayBase';
import { ModalDialogBody } from './Body';
import { ModalDialogFooter } from './Footer';
import { ModalDialogHeader } from './Header';
import { ModalDialogTrigger } from './Trigger';

import styles from './ModalDialog.module.scss';

export type ModalDialogRootProps = {
  /** The description of the dialog to identify the element to assistive technology. It must be provided if you don't use `ModalDialog.ContentHeader` and `ModalDialog` does not contain a visible heading. */
  'aria-label'?: DialogProps['aria-label'];
  /** The ID of an element that labels the modal. It must be provided if you use a custom heading instead of using `ModalDialog.Header`. */
  'aria-labelledby'?: DialogProps['aria-labelledby'];
  /** The children to render inside the component. Should be `ModalDialog.Content`. */
  children: DialogProps['children'];
  /** Optional property for overriding styles. Styles will apply to the modal overlay (i.e. the outer layer of the white box), not the overlay (i.e the darkened background) or the dialog `section`. */
  className?: ReactAriaModalOverlayProps['className'];
  /** Indicates whether `ModalDialog` can be closed. When `false`, no close button will render and clicking the overlay will not close `Modal`. The code rendering `ModalDialog` must manually close it. */
  isDismissable?: ReactAriaModalOverlayProps['isDismissable'];
  /** Whether the modal overlay is open. Should only be passed when the component's state is controlled. */
  isOpen?: ReactAriaModalOverlayProps['isOpen'];
  /** Handler called when the overlay's open state changes. */
  onOpenChange?: ReactAriaModalOverlayProps['onOpenChange'];
  /** Optional property for overriding styles. Styles will apply to the modal overlay (i.e. the outer layer of the white box), not the overlay (i.e the darkened background) or the dialog `section`. */
  style?: ReactAriaModalOverlayProps['style'];
};

/**
 * `ModalDialog` is a windows that appears over the user interface.
 * Built using React Aria's [Modal](https://react-spectrum.adobe.com/react-aria/Modal.html) and [Dialog](https://react-spectrum.adobe.com/react-aria/Dialog.html) components.
 */
export const ModalDialogRoot = ({
  children,
  className,
  isDismissable = true,
  isOpen,
  onOpenChange,
  style,
  ...props
}: ModalDialogRootProps) => {
  return (
    <OverlayBase
      className={className}
      isDismissable={isDismissable}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      style={style}
    >
      <ModalDialogInner
        aria-label={props['aria-label']}
        aria-labelledby={props['aria-labelledby']}
      >
        {children}
      </ModalDialogInner>
    </OverlayBase>
  );
};

type ModalDialogInnerProps = Pick<
  DialogBaseProps,
  'aria-label' | 'aria-labelledby' | 'children' | 'className'
>;

const ModalDialogInner = ({ children, ...props }: ModalDialogInnerProps) => {
  const state = useContext(ReactAriaOverlayTriggerStateContext);
  const closeOverlay = React.useCallback(() => state?.close(), [state]);

  const modalContext = useContext(ModalDialogContext);

  return (
    <DialogBase
      aria-label={props['aria-label']}
      aria-labelledby={props['aria-labelledby']}
    >
      {(dialogRenderProps) => (
        <>
          {modalContext?.isDismissable && (
            <IconButton
              aria-label="Close modal"
              className={styles.closeButton}
              name="formX"
              onPress={closeOverlay}
              variant="ghost"
            />
          )}
          {typeof children === 'function'
            ? children(dialogRenderProps)
            : children}
        </>
      )}
    </DialogBase>
  );
};

export const ModalDialog = {
  /**
   * The dialog's main content.
   */
  Body: ModalDialogBody,
  /**
   * The dialog footer, which should contain up to two buttons:
   * - an optional secondary button
   * - an required primary button
   */
  Footer: ModalDialogFooter,
  /**
   * The dialog header containing a descriptive title of the dialog content.
   */
  Header: ModalDialogHeader,
  /**
   * A modal dialog shown above other content in an application.
   */
  Root: ModalDialogRoot,
  /**
   * A trigger container that listens for a button press to open the modal dialog. It should wrap around a `design-system` `Button` and `Modal.Root`.
   *
   * **Should only be used when the `ModalDialog`'s open state is uncontrolled**
   */
  Trigger: ModalDialogTrigger,
};

export default ModalDialog;
